/**
 * Shrinking a phone photo before it leaves the phone.
 *
 * A modern phone takes 4–12 MB pictures; sent as they are, one walk-round
 * would be 50 MB over a mobile connection in the yard. Redrawn at 1600 px on
 * the long side they stay sharp enough to see a scratch and weigh a few
 * hundred kilobytes, and a 360 px copy is made alongside for lists.
 *
 * Redrawing also settles two things on the way: the picture's rotation (the
 * phone stores it as a flag the browser applies when decoding), and HEIC,
 * which Safari decodes but the server would not know what to do with — what
 * comes out is always JPEG.
 */
const FULL_EDGE = 1600;
const THUMB_EDGE = 360;
const FULL_QUALITY = 0.82;
const THUMB_QUALITY = 0.7;

export interface ShrunkPhoto {
  image: string;
  thumb: string;
  width: number;
  height: number;
  /** For showing straight away, before the upload finishes. */
  previewUrl: string;
}

async function decode(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Older Safari refuses the options object; the <img> path below copes.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function draw(source: ImageBitmap | HTMLImageElement, edge: number, quality: number) {
  const width = 'naturalWidth' in source ? source.naturalWidth : source.width;
  const height = 'naturalHeight' in source ? source.naturalHeight : source.height;
  const scale = Math.min(1, edge / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas is not available');
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return {
    canvas,
    blob: new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the photo'))),
        'image/jpeg', quality);
    }),
  };
}

async function toBase64(blob: Blob): Promise<string> {
  const buffer = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < buffer.length; i += chunk) {
    binary += String.fromCharCode(...buffer.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export async function shrinkPhoto(file: Blob): Promise<ShrunkPhoto> {
  const source = await decode(file);
  try {
    const full = draw(source, FULL_EDGE, FULL_QUALITY);
    const small = draw(source, THUMB_EDGE, THUMB_QUALITY);
    const [fullBlob, thumbBlob] = await Promise.all([full.blob, small.blob]);
    return {
      image: await toBase64(fullBlob),
      thumb: await toBase64(thumbBlob),
      width: full.canvas.width,
      height: full.canvas.height,
      previewUrl: URL.createObjectURL(thumbBlob),
    };
  } finally {
    if ('close' in source) source.close();
  }
}

/** Where the API serves a stored photo. Same origin, so the session cookie goes with it. */
export const photoUrl = (id: string, size: 'full' | 'thumb' = 'full'): string =>
  `/api/admin/inspection-photos/${id}${size === 'thumb' ? '?size=thumb' : ''}`;
